import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UsersThree, Plus, ShieldCheck } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { dateShort } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

export default function Team() {
  const { isOwner, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "underwriter" });

  const load = () => { api.get("/team").then((r) => setMembers(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/team", form);
      toast.success("Team member created");
      setOpen(false); setForm({ name: "", email: "", password: "", role: "underwriter" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleActive = async (m) => {
    try { await api.patch(`/team/${m.id}`, { active: !m.active }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setRole = async (m, role) => {
    try { await api.patch(`/team/${m.id}`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-5 md:p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline text-primary">Team</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Team Members</h1>
        </div>
        {isOwner && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="invite-member-button"><Plus size={18} className="mr-1" /> Add member</Button>
            </DialogTrigger>
            <DialogContent className="theme-admin bg-card border-border">
              <DialogHeader><DialogTitle>Add team member</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="overline text-muted-foreground">Name</Label><Input className="bg-secondary border-border mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-name" /></div>
                <div><Label className="overline text-muted-foreground">Email</Label><Input className="bg-secondary border-border mt-1" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-email" /></div>
                <div><Label className="overline text-muted-foreground">Temp Password</Label><Input className="bg-secondary border-border mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="member-password" /></div>
                <div><Label className="overline text-muted-foreground">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="bg-secondary border-border mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="underwriter">Underwriter</SelectItem><SelectItem value="owner">Owner</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="create-member-button">Create member</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="mt-6 rounded-[var(--radius)] border border-border bg-card overflow-hidden">
        {members.map((m) => (
          <div key={m.id} className="flex items-center gap-4 px-5 py-4 border-b border-border/60 last:border-0" data-testid={`member-${m.id}`}>
            <div className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center font-semibold">{m.name?.[0]}</div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm flex items-center gap-2">{m.name} {m.role === "owner" && <ShieldCheck size={14} weight="fill" className="text-primary" />}</div>
              <div className="text-xs text-muted-foreground truncate">{m.email} · joined {dateShort(m.created_at)}</div>
            </div>
            {isOwner ? (
              <Select value={m.role} onValueChange={(v) => setRole(m, v)} disabled={m.id === user.id}>
                <SelectTrigger className="w-36 bg-secondary border-border h-9"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="underwriter">Underwriter</SelectItem><SelectItem value="owner">Owner</SelectItem></SelectContent>
              </Select>
            ) : (
              <span className="text-sm capitalize text-muted-foreground">{m.role}</span>
            )}
            {isOwner && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Switch checked={m.active} onCheckedChange={() => toggleActive(m)} disabled={m.id === user.id} data-testid={`toggle-active-${m.id}`} />
                {m.active ? "Active" : "Inactive"}
              </label>
            )}
          </div>
        ))}
      </div>
      {!isOwner && <p className="text-xs text-muted-foreground mt-3">Only owners can add or manage team members.</p>}
    </div>
  );
}
